import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Plant, ArrowRight, BookOpen } from "@phosphor-icons/react";
import { IS_FARMERSPULSE, SITE_META } from "@/lib/site";
import { coverFor } from "@/lib/guide_covers";
import SEOHead from "@/components/SEOHead";

/**
 * Public index for /guides — every published `page_type=guide` CMS
 * document ordered by `sort_order`. Cards deep-link to /guides/:slug.
 */
export default function GuideIndex() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    http.get("/cms/pages?page_type=guide&limit=100")
      .then(({ data }) => { if (!cancelled) setPages(data.pages || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { document.title = `Guides · ${SITE_META.brand}`; }, []);

  const base = IS_FARMERSPULSE ? "/guides" : "/farmers/guides";

  return (
    <main
      data-testid="guides-index"
      className="min-h-screen pt-24 md:pt-28 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#241209]"
    >
      <SEOHead
        title="How FarmersPulse Works — 16 Feature Guides | FarmersPulse™"
        description="Every FarmersPulse feature explained in one place — Weather Today, Rainfall, Irrigation, Growing Conditions, Farm Alerts, What to Plant, What to Harvest, Pest & Disease Alerts, My Farm, Farm Diary, Farm Calendar, Agricultural News, Ask FarmersPulse, Global Farming and Farmers Pulse Today."
        path="/guides"
      />
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#2D8659)] flex items-center gap-1.5">
          <Plant size={12} weight="fill" /> {SITE_META.brand} · Feature Guides
        </div>
        <h1 className="mt-3 font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter text-[#151312]">
          How FarmersPulse works.
        </h1>
        <p className="mt-5 text-lg text-[#3A3830] max-w-2xl">
          Local observations + weather data + farmer knowledge + AI interpretation
          = better-informed farming decisions. Every tab, explained.
        </p>

        <div className="mt-12 space-y-3" data-testid="guides-index-list">
          {loading ? (
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#8E8B7F]">Loading…</div>
          ) : pages.length === 0 ? (
            <div className="rounded-2xl border border-black/10 bg-white/60 p-6 text-[#3A3830]">
              Guides are being finalized. Please check back shortly.
            </div>
          ) : pages.map((p) => (
            <Link
              key={p.slug}
              to={`${base}/${p.slug}`}
              data-testid={`guides-index-item-${p.slug}`}
              className="group block rounded-2xl border border-black/10 bg-white/70 p-5 md:p-6 hover:border-[color:var(--np-brand,#2D8659)] hover:bg-white transition-colors"
            >
              <div className="flex items-start gap-3">
                <BookOpen size={18} weight="regular" className="mt-1 text-[color:var(--np-brand,#2D8659)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight text-[#151312] group-hover:text-[color:var(--np-brand,#2D8659)]">
                    {p.title}
                  </div>
                  {p.subtitle && (
                    <div className="mt-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8E8B7F]">
                      {p.subtitle}
                    </div>
                  )}
                  {p.excerpt && (
                    <p className="mt-2 text-sm text-[#3A3830] leading-relaxed">{p.excerpt}</p>
                  )}
                </div>
                <ArrowRight size={16} weight="bold" className="mt-1 text-[#3A3830] group-hover:text-[color:var(--np-brand,#2D8659)] flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
